// CCTV API Service - Real API calls for CCTV Dashboard
import axios from 'axios';
import { APP_BASE_URL, GET_LATEST_TELEMETRY, GET_DEVICE_BY_CUSTOMER_ID, trimAssetName } from '../../../Common/EndPoints';
import { CCTVCamera, CCTVStats, StationStats } from './cctvTypes';
import moment from 'moment';

// Get the CCTV system ID from systems list
// Searches by code or name patterns that indicate a CCTV/Camera system
export const getCCTVSystemId = (): string | null => {
  try {
    const systemsList = JSON.parse(localStorage.getItem('systems_list') || '[]');

    if (!systemsList || systemsList.length === 0) {
      console.warn('No systems_list found in localStorage');
      return null;
    }

    // Search by code or name/alias patterns
    const cctvSystem = systemsList.find((sys: any) =>
      sys.code?.toUpperCase().includes('CCTV') ||
      sys.code?.toUpperCase().includes('CAMERA') ||
      sys.code?.toUpperCase().includes('ICCC') ||
      sys.alias?.toUpperCase().includes('CCTV') ||
      sys.alias?.toUpperCase().includes('CAMERA') ||
      sys.name?.toUpperCase().includes('CCTV') ||
      sys.name?.toUpperCase().includes('CAMERA') ||
      sys.name?.toUpperCase().includes('ICCC') ||
      sys.name?.toUpperCase().includes('SURVEILLANCE') ||
      sys.name?.toUpperCase().includes('VIDEO')
    );

    if (cctvSystem) {
      console.log('Found CCTV system:', cctvSystem.name, '- ID:', cctvSystem.id);
      return cctvSystem.id;
    }

    console.warn('No CCTV system found in systems_list. Available systems:',
      systemsList.map((s: any) => ({ code: s.code, name: s.name }))
    );
    return null;
  } catch (e) {
    console.error('Error getting CCTV system ID:', e);
    return null;
  }
};

// Get customer IDs from localStorage
export const getCustomerIds = (): string[] => {
  try {
    const customersList = JSON.parse(localStorage.getItem('selected_customers') || '[]');
    if (customersList.length > 0) {
      return customersList.map((c: any) => c.customerId || c.id);
    }
    return [];
  } catch {
    return [];
  }
};

// Fetch CCTV device types
export const fetchCCTVDeviceTypes = async (systemId: string, customerIds: string[]): Promise<any[]> => {
  try {
    const url = `${APP_BASE_URL}${GET_DEVICE_BY_CUSTOMER_ID}`;
    const params = { softwareIds: systemId };
    const response = await axios.post(url, customerIds, { params });
    return response?.data?.data || [];
  } catch (error) {
    console.error('Error fetching CCTV device types:', error);
    return [];
  }
};

// Parse device name to extract Zone, Division, Station, Device Name
// Format: ZONE_DIVISION_STATION_DEVICENAME (e.g., NCR_PRYJ_PRYJSTN_CAMERA1)
const parseDeviceName = (fullName: string) => {
  const parts = fullName.split('_');
  const trimmedName = trimAssetName(fullName);

  let zone = '-';
  let division = '-';
  let station = '-';
  let deviceName = trimmedName;

  if (parts.length >= 4) {
    zone = parts[0] || '-';
    division = parts[1] || '-';
    station = parts[2] || '-';
    deviceName = parts.slice(3).join('-') || trimmedName;
  } else if (parts.length === 3) {
    zone = parts[0] || '-';
    division = parts[1] || '-';
    station = parts[2] || '-';
  } else if (parts.length === 2) {
    station = parts[0] || '-';
    deviceName = parts[1] || trimmedName;
  }

  return { zone, division, station, deviceName };
};

// Transform raw telemetry data to CCTVCamera format
const transformTelemetryToCamera = (rawData: any, index: number): CCTVCamera => {
  const fullDeviceName = rawData.deviceName || '';
  const { zone, division, station, deviceName: parsedDeviceName } = parseDeviceName(fullDeviceName);
  const dataLists = rawData.dataLists || [];

  // Merge all data packets from the API response (same pattern as VoIP dashboard)
  let mergedData: Record<string, any> = {};
  let lastActivityTime = rawData.ts || 0;

  dataLists.forEach((item: any) => {
    const data = item?.data || {};
    Object.entries(data).forEach(([key, value]: [string, any]) => {
      // Handle nested objects like additionalInfo and alertInfo
      const val = value?.[0]?.value;
      if (key === 'additionalInfo' && typeof val === 'object') {
        mergedData['additionalInfo'] = val;
      } else if (key === 'alertInfo' && typeof val === 'object') {
        mergedData['alertInfo'] = val;
      } else {
        mergedData[key] = val ?? '';
      }

      const ts = value?.[0]?.ts;
      if (ts && Number(ts) > lastActivityTime) {
        lastActivityTime = Number(ts);
      }
    });
  });

  // Get device name - use NAME field or parsed name
  const actualDeviceName =
    mergedData['NAME'] ||
    mergedData['asset.name'] ||
    parsedDeviceName ||
    fullDeviceName;

  // Get station from STN or STATION field or parsed
  const finalStation =
    mergedData['STN'] ||
    mergedData['STATION'] ||
    mergedData['asset.station'] ||
    station ||
    '-';

  const finalZone = mergedData['asset.zone'] || mergedData['ZONE'] || zone || '-';
  const finalDivision = mergedData['asset.division'] || mergedData['DIVISION'] || division || '-';

  // Determine camera status based on actual API response fields
  // From API: PFC_In can be "Active" or other values
  // Also check: asset.health, dataList status, EVENTTYPE

  const pfcIn = (mergedData['PFC_In'] || '').toString().toUpperCase();
  const dataListStatus = (dataLists[0]?.status || '').toString().toUpperCase();
  const assetHealth = (mergedData['asset.health'] || '').toString().toUpperCase();

  // Camera is Working if:
  // 1. PFC_In is "ACTIVE" (from actual API response)
  // 2. dataList status is "NORMAL"
  // 3. asset.health is "HEALTHY"
  // 4. Has recent data (ts exists and is recent)
  const hasRecentData = lastActivityTime > 0;
  const isActive = pfcIn === 'ACTIVE'
    // || dataListStatus === 'NORMAL'
    // || assetHealth === 'HEALTHY'
    // || hasRecentData;

  return {
    id: rawData.deviceId || `cam-${index}`,
    deviceId: rawData.deviceId || `CAM-${index}`,
    name: actualDeviceName,
    station: finalStation,
    zone: finalZone,
    division: finalDivision,
    status: isActive ? 'active' : 'inactive',
    lastActivity: lastActivityTime > 0 ? moment(lastActivityTime).toISOString() : new Date().toISOString(),
  };
};

// Fetch CCTV cameras from telemetry API
export const fetchCCTVCameras = async (
  customerIds: string[],
  systemId: string | null
): Promise<CCTVCamera[]> => {
  try {
    if (!systemId || customerIds.length === 0) {
      console.warn('Missing systemId or customerIds for CCTV fetch');
      return [];
    }

    console.log('Fetching CCTV cameras with systemId:', systemId, 'customerIds:', customerIds.length);

    // First, get device types for this system to find the correct deviceCategory
    const deviceTypesUrl = `${APP_BASE_URL}${GET_DEVICE_BY_CUSTOMER_ID}`;
    const deviceTypesResponse = await axios.post(deviceTypesUrl, customerIds, {
      params: { softwareIds: systemId }
    });
    const deviceTypes = deviceTypesResponse?.data?.data || [];
    console.log('CCTV Device types available:', deviceTypes);

    // Find camera device type - could be "CAMERA", "CAM", "CCTV", etc.
    const cameraDeviceType = deviceTypes.find((dt: any) =>
      dt.label?.toUpperCase().includes('CAMERA') ||
      dt.label?.toUpperCase().includes('CAM') ||
      dt.label?.toUpperCase().includes('CCTV')
    );

    // Use the found device type label, or null to get all devices for this system
    const deviceCategory = cameraDeviceType?.label || null;
    console.log('Using deviceCategory:', deviceCategory);

    // Fetch telemetry with same payload format as Telemetry page
    const payload = {
      customerIds: customerIds,
      deviceCategory: deviceCategory,
      status: 'ALL',
      softwareId: systemId,
    };

    const url = `${APP_BASE_URL}${GET_LATEST_TELEMETRY}`;
    const response = await axios.post(url, payload);

    const rawData = response?.data?.data || [];
    console.log('CCTV API Response:', rawData.length, 'cameras found', rawData);
    return rawData.map((item: any, index: number) => transformTelemetryToCamera(item, index));
  } catch (error) {
    console.error('Error fetching CCTV cameras:', error);
    return [];
  }
};

// Calculate stats from cameras
export const calculateCCTVStats = (cameras: CCTVCamera[]): CCTVStats => {
  const activeCameras = cameras.filter((c) => c.status === 'active').length;
  const inactiveCameras = cameras.filter((c) => c.status === 'inactive').length;

  return {
    totalCameras: cameras.length,
    activeCameras,
    inactiveCameras,
  };
};

// Calculate station-wise statistics
export const calculateStationStats = (cameras: CCTVCamera[]): StationStats[] => {
  const stationMap = new Map<string, StationStats>();

  cameras.forEach((cam) => {
    const existing = stationMap.get(cam.station) || {
      station: cam.station,
      total: 0,
      active: 0,
      inactive: 0,
    };

    existing.total++;
    if (cam.status === 'active') {
      existing.active++;
    } else {
      existing.inactive++;
    }

    stationMap.set(cam.station, existing);
  });

  return Array.from(stationMap.values()).sort((a, b) => a.station.localeCompare(b.station));
};

// Format timestamp for display
export const formatTimestamp = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'Asia/Kolkata',
    }).replace(',', '');
  } catch {
    return isoString;
  }
};

// Get relative time
export const getRelativeTime = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} mins ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } catch {
    return 'Unknown';
  }
};
